import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";

const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export default function JobCardCashForm() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [paymentType, setPaymentType] = useState("normal");
  const [searchTerm, setSearchTerm] = useState("");
  const [worker, setWorker] = useState(null);
  const [payees, setPayees] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [lotId, setLotId] = useState("");
  const [details, setDetails] = useState("");
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedPayment, setSavedPayment] = useState(null);
  const submitting = useRef(false);
  const searchVersion = useRef(0);
  const workerInput = useRef(null);
  const lotInput = useRef(null);
  const amountInput = useRef(null);

  const multiplier = paymentType === "normal" ? 1 : 1.5;
  const basePaise = Math.round(Number(amount) * 100);
  const payablePaise = Math.round(basePaise * multiplier);
  const validAmount = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(amount)
    && Number.isSafeInteger(basePaise) && Number.isSafeInteger(payablePaise) && basePaise > 0
    && Number(amount) <= 99999999.99;
  const payableAmount = validAmount ? payablePaise / 100 : 0;
  const busy = confirming || saving;

  useEffect(() => {
    setPayees([]);
    setSearching(false);
    setSearchError("");
    if (worker || searchTerm.trim().length < 2) return undefined;

    let cancelled = false;
    const version = searchVersion.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axiosInstance.get("/api/job-card-cash/payee-search", {
          params: { q: searchTerm.trim() },
        });
        if (!cancelled && version === searchVersion.current) {
          const results = Array.isArray(response.data) ? response.data : [];
          setPayees(results);
          if (!results.length) setSearchError("No matching worker found. Try another Logic Code or Tally Name.");
        }
      } catch (_) {
        if (!cancelled && version === searchVersion.current) {
          setSearchError("Could not load worker details. Please try again.");
        }
      } finally {
        if (!cancelled && version === searchVersion.current) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, worker]);

  const clearFeedback = field => {
    setSaveError("");
    setErrors(current => ({ ...current, [field]: "" }));
  };

  const selectWorker = payee => {
    searchVersion.current += 1;
    setWorker(payee);
    setSearchTerm(`${payee.LOGIC_ACCOUNT_CODE} - ${payee.LOGIC_ACCOUNT_NAME}`);
    setPayees([]);
    setSearching(false);
    clearFeedback("worker");
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    const nextErrors = {};
    if (!worker?.WORKER_CODE) nextErrors.worker = "Select a worker from the search results.";
    if (!lotId.trim()) nextErrors.lotId = "Lot ID is required.";
    else if (lotId.trim().length > 500) nextErrors.lotId = "Lot ID must be at most 500 characters.";
    if (!validAmount) nextErrors.amount = "Enter a positive amount up to 99,999,999.99 with up to two decimal places.";
    setErrors(nextErrors);
    setSaveError("");
    setSavedPayment(null);
    if (Object.keys(nextErrors).length) {
      if (nextErrors.worker) workerInput.current?.focus();
      else if (nextErrors.lotId) lotInput.current?.focus();
      else amountInput.current?.focus();
      return;
    }
    const payment = {
      logicAccountCode: String(worker.LOGIC_ACCOUNT_CODE),
      lotId: lotId.trim(),
      details: details.trim(),
      paymentType,
      baseAmount: (basePaise / 100).toFixed(2),
    };
    submitting.current = true;
    setConfirming(true);
    try {
      const confirmation = await Swal.fire({
        title: "Submit cash payment?",
        text: `Worker: ${worker.WORKER_NAME}\nLot ID: ${payment.lotId}\nPayment Type: ${paymentType === "normal" ? "Normal" : "1.5×"}\nPayable Amount: ${money.format(payableAmount)}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, submit",
        cancelButtonText: "Cancel",
        focusCancel: true,
      });
      if (!confirmation.isConfirmed) return;
      setConfirming(false);
      setSaving(true);
      const response = await axiosInstance.post("/api/job-card-cash/submit", payment);
      if (!response.data?.success) throw new Error(response.data?.error || "Payment could not be saved.");
      resetForm();
      setSavedPayment({ id: response.data.id, amount: response.data.amount });
    } catch (error) {
      setSaveError(error.response?.data?.error || error.message || "Payment could not be saved.");
    } finally {
      submitting.current = false;
      setConfirming(false);
      setSaving(false);
    }
  };

  const resetForm = () => {
    searchVersion.current += 1;
    setPaymentType("normal");
    setSearchTerm("");
    setWorker(null);
    setPayees([]);
    setSearching(false);
    setSearchError("");
    setLotId("");
    setDetails("");
    setAmount("");
    setErrors({});
    setSaveError("");
    setSavedPayment(null);
  };

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}><Sidebar /></div>
      <div className="flex-grow-1">
        <Header toggleSidebar={() => setIsSidebarVisible(current => !current)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-3 p-md-4">
          <div className="mx-auto" style={{ maxWidth: 1100 }}>
            <h3 className="mb-1">Job Card Payment Cash</h3>
            <p className="text-muted mb-4">Select a worker and enter the lot and payment details.</p>
            {savedPayment && <div className="alert alert-success" role="status">Payment saved successfully. Entry #{savedPayment.id} · {money.format(savedPayment.amount)}</div>}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-dark text-white py-3">Cash Payment Details</div>
              <div className="card-body p-3 p-md-4">
                <form onSubmit={handleSubmit} noValidate>
                  <fieldset disabled={busy}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="job-cash-type" className="form-label">Payment Type <span className="text-danger">*</span></label>
                      <select id="job-cash-type" className="form-select" value={paymentType} onChange={event => { setPaymentType(event.target.value); clearFeedback("paymentType"); }}>
                        <option value="normal">Normal</option>
                        <option value="1.5x">1.5×</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="job-cash-lot" className="form-label">Lot ID <span className="text-danger">*</span></label>
                      <input ref={lotInput} id="job-cash-lot" className={`form-control ${errors.lotId ? "is-invalid" : ""}`} value={lotId} maxLength={500} onChange={event => { setLotId(event.target.value); clearFeedback("lotId"); }} placeholder="Enter Lot ID" required aria-invalid={Boolean(errors.lotId)} aria-describedby={errors.lotId ? "job-cash-lot-error" : undefined} />
                      {errors.lotId && <div id="job-cash-lot-error" className="invalid-feedback">{errors.lotId}</div>}
                    </div>

                    <div className="col-12">
                      <label htmlFor="job-cash-worker" className="form-label">Search Logic Code / Tally Name <span className="text-danger">*</span></label>
                      <input ref={workerInput} id="job-cash-worker" className={`form-control ${errors.worker ? "is-invalid" : ""}`} value={searchTerm} autoComplete="off" placeholder="Type at least 2 characters, then select a worker" aria-invalid={Boolean(errors.worker)} aria-describedby={errors.worker ? "job-cash-worker-error" : undefined} onChange={event => {
                        searchVersion.current += 1;
                        setSearchTerm(event.target.value);
                        setWorker(null);
                        setPayees([]);
                        setSearching(false);
                        setSearchError("");
                        clearFeedback("worker");
                      }} />
                      {errors.worker && <div id="job-cash-worker-error" className="invalid-feedback">{errors.worker}</div>}
                      <div aria-live="polite">
                        {searching && <div className="text-muted small mt-2">Searching workers…</div>}
                        {searchError && <div className="text-danger small mt-2">{searchError}</div>}
                      </div>
                      {payees.length > 0 && <div className="list-group mt-1" style={{ maxHeight: 220, overflowY: "auto" }} aria-label="Worker search results">
                        {payees.map((payee, index) => <button key={`${payee.LOGIC_ACCOUNT_CODE}-${index}`} type="button" className="list-group-item list-group-item-action" onClick={() => selectWorker(payee)}>
                          {payee.LOGIC_ACCOUNT_CODE} — {payee.LOGIC_ACCOUNT_NAME}
                        </button>)}
                      </div>}
                    </div>

                    {[
                      ["WORKER_CODE", "Worker Code"], ["WORKER_NAME", "Worker Name"],
                      ["LOGIC_ACCOUNT_CODE", "Logic Account Code"], ["LOGIC_ACCOUNT_NAME", "Logic Account Name"],
                      ["BANK_ACCOUNT_NO", "Bank Account Number"], ["IFSC_CODE", "IFSC Code"],
                      ["Gender", "Gender"], ["Department", "Department"],
                    ].map(([key, label]) => <div key={key} className="col-md-6 col-lg-3">
                      <label htmlFor={`job-cash-${key}`} className="form-label">{label}</label>
                      <input id={`job-cash-${key}`} className="form-control bg-light" value={worker?.[key] || ""} placeholder={worker ? "Not available in master" : ""} readOnly />
                    </div>)}

                    <div className="col-md-6">
                      <label htmlFor="job-cash-amount" className="form-label">Base Amount (₹) <span className="text-danger">*</span></label>
                      <input ref={amountInput} id="job-cash-amount" type="text" inputMode="decimal" className={`form-control ${errors.amount ? "is-invalid" : ""}`} value={amount} placeholder="0.00" required aria-invalid={Boolean(errors.amount)} aria-describedby={errors.amount ? "job-cash-amount-error" : undefined} onChange={event => {
                        if (event.target.value === "" || /^\d*(\.\d{0,2})?$/.test(event.target.value)) {
                          setAmount(event.target.value);
                          clearFeedback("amount");
                        }
                      }} />
                      {errors.amount && <div id="job-cash-amount-error" className="invalid-feedback">{errors.amount}</div>}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="job-cash-payable" className="form-label">Payable Amount (₹)</label>
                      <input id="job-cash-payable" className="form-control bg-light fw-bold" value={money.format(payableAmount)} readOnly />
                      <div className="form-text">Base amount × {multiplier}{paymentType === "1.5x" ? " (rounded to the nearest paise)" : ""}</div>
                    </div>
                    <div className="col-12">
                      <label htmlFor="job-cash-details" className="form-label">Other Details <span className="text-muted small">(optional)</span></label>
                      <textarea id="job-cash-details" className="form-control" rows={3} value={details} maxLength={2000} placeholder="Add job card or payment details" onChange={event => { setDetails(event.target.value); clearFeedback("details"); }} />
                    </div>
                  </div>
                  <div className="d-flex gap-2 justify-content-end mt-4">
                    <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Clear</button>
                    <button type="submit" className="btn btn-primary" disabled={busy}>{saving ? "Saving…" : confirming ? "Confirming…" : "Submit"}</button>
                  </div>
                  </fieldset>
                </form>
                {saveError && <div className="alert alert-danger mt-3 mb-0" role="alert">{saveError}</div>}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
